import React, { useState, useEffect, useRef, useContext } from "react";
import * as S from "./AiChatStyle";
import { aiService } from "../../../Services/aiService";
import { AuthContext } from "../../../context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function AiChat() {
  const { userData, sessionId, token, generateAiSession } =
    useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const STORAGE_KEY = `golden_ai_chat_history_${userData?.id}`;

  const [messages, setMessages] = useState(() => {
    try {
      const savedMessages = sessionStorage.getItem(STORAGE_KEY);
      if (savedMessages) return JSON.parse(savedMessages);
    } catch (error) {
      console.error("Erro ao carregar chat:", error);
    }
    return [
      {
        id: 1,
        text: "Olá! Sou o seu assistente Golden. Como posso ajudar?",
        isUser: false,
      },
    ];
  });

  const scrollRef = useRef(null);

  useEffect(() => {
    if (messages?.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages, STORAGE_KEY]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isTyping]);

  const handleClearChat = () => {
    if (
      window.confirm(
        "Deseja iniciar uma nova conversa? Isso resetará o contexto da IA."
      )
    ) {
      const initialMsg = [
        {
          id: Date.now(),
          text: "Olá! Sou o seu assistente Golden. Como posso ajudar?",
          isUser: false,
        },
      ];
      setMessages(initialMsg);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(initialMsg));
      generateAiSession(); // Reseta o thread_id no contexto
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !token) return;

    const userMsg = { id: Date.now(), text: inputValue, isUser: true };
    setMessages((prev) => [...prev, userMsg]);

    const messageToSend = inputValue;
    setInputValue("");
    setIsTyping(true);

    try {
      const data = await aiService.sendMessage(messageToSend, sessionId, token);

      const aiMsg = {
        id: Date.now() + 1,
        text: data.response || "Não entendi, pode repetir?",
        isUser: false,
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "❌ Erro ao comunicar com a IA. Tente novamente mais tarde.",
          isUser: false,
          isError: true,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const formatMessage = (text) => {
    if (!text) return "";
    // Limpeza de tags personalizadas caso o backend ainda envie
    const cleanText = text
      .replace(/<negrito>/g, "**")
      .replace(/<\/negrito>/g, "**")
      .replace(/<italico>/g, "*")
      .replace(/<\/italico>/g, "*");

    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanText}</ReactMarkdown>
    );
  };

  return (
    <S.ChatWrapper>
      <S.ChatContainer isOpen={isOpen}>
        <S.ChatHeader>
          <div className="title">
            <img src="/logo-golden.png" alt="IA" />
            <span>Golden AI</span>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button
              onClick={handleClearChat}
              title="Nova Conversa (Reset)"
              style={{ fontSize: "16px" }}
            >
              🔄
            </button>
            <button
              onClick={() => setIsOpen(false)}
              style={{ fontSize: "28px" }}
            >
              ×
            </button>
          </div>
        </S.ChatHeader>

        <S.MessageList ref={scrollRef}>
          {messages.map((msg) => (
            <S.MessageBubble
              key={msg.id}
              isUser={msg.isUser}
              isError={msg.isError}
            >
              {formatMessage(msg.text)}
            </S.MessageBubble>
          ))}
          {isTyping && (
            <S.MessageBubble isUser={false}>
              <span style={{ fontStyle: "italic", color: "#d4af37" }}>
                Golden está digitando...
              </span>
            </S.MessageBubble>
          )}
        </S.MessageList>

        <S.InputArea onSubmit={handleSendMessage}>
          <input
            placeholder="Pergunte sobre seus contratos..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isTyping}
          />
          <button type="submit" disabled={isTyping}>
            <span>→</span>
          </button>
        </S.InputArea>
      </S.ChatContainer>

      <S.ToggleChatBtn onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? (
          <span>×</span>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.4876 3.36093 14.891 4 16.1272L3 21L7.8728 20C9.10904 20.6391 10.5124 21 12 21Z"
              stroke="#d4af37"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 9H16"
              stroke="#d4af37"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M8 13H13"
              stroke="#d4af37"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </S.ToggleChatBtn>
    </S.ChatWrapper>
  );
}
